import { useEffect, useState } from 'react';
import GameState from '~/modules/GameEngine/GameState/GameState';
import { useMyPlayerId } from '~/modules/Multiplayer/hooks/useIsHost';
import { LeaderboardEntry, useMultiplayerScores } from '~/modules/Multiplayer/hooks/useMultiplayerScores';
import { playerColor } from '~/modules/Multiplayer/playerColors';
import RoomStateStore from '~/modules/Multiplayer/RoomState';
import ScoreText from './ScoreText';

const MAX_ROWS = 8;
const LOCAL_SCORE_REFRESH_MS = 250;

// The server sends everyone's scores about once a second; our own score comes from the local engine
// so the ranking never shows a different number than the big score on the right.
function useLocalScore() {
  const [score, setScore] = useState(() => GameState.getPlayerScore(0));
  useEffect(() => {
    const id = setInterval(() => setScore(GameState.getPlayerScore(0)), LOCAL_SCORE_REFRESH_MS);
    return () => clearInterval(id);
  }, []);
  return score;
}

function MultiplayerLeaderboard() {
  const serverScores = useMultiplayerScores();
  const myId = useMyPlayerId();
  const localScore = useLocalScore();

  if (!RoomStateStore.isInRoom || serverScores.length === 0) return null;

  const scores = serverScores
    .map((s) => (s.id === myId ? { ...s, score: Math.max(s.score, localScore) } : s))
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));

  // Top rows + the current player if they are further down
  const top = scores.slice(0, MAX_ROWS);
  const myRank = scores.findIndex((s) => s.id === myId) + 1;
  const myEntry = myRank > MAX_ROWS ? scores[myRank - 1] : undefined;

  return (
    <div
      data-test="mp-leaderboard"
      className="pointer-events-none absolute top-14 left-4 z-[100] flex w-64 flex-col gap-0.5 rounded-lg bg-black/70 p-2 backdrop-blur-sm">
      <div className="mb-1 flex px-1 text-[11px] font-bold tracking-wider text-white/50 uppercase">
        <span>Ranking</span>
        <span className="ml-auto">{scores.length} graczy</span>
      </div>
      {top.map((entry, i) => (
        <LeaderboardRow key={entry.id} entry={entry} rank={i + 1} isMe={entry.id === myId} />
      ))}
      {myEntry && (
        <>
          <div className="text-center text-xs leading-3 text-white/40">···</div>
          <LeaderboardRow entry={myEntry} rank={myRank} isMe />
        </>
      )}
    </div>
  );
}

function LeaderboardRow({ entry, rank, isMe }: { entry: LeaderboardEntry; rank: number; isMe: boolean }) {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  const color = playerColor(entry.color);

  return (
    <div
      data-test="mp-leaderboard-row"
      data-name={entry.name}
      className={`flex items-center gap-2 rounded px-1.5 py-0.5 text-sm ${isMe ? 'bg-white/20 font-bold text-white' : 'text-white/85'} ${
        entry.connected ? '' : 'opacity-40'
      }`}>
      <span className="w-6 shrink-0 text-center text-xs">{medal || `#${rank}`}</span>
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color.hex }} />
      <span className="min-w-0 flex-1 truncate">
        {entry.name}
        {isMe && <span className="font-normal text-white/60"> (Ty)</span>}
      </span>
      {entry.finished && <span title="skończył(a) piosenkę">✓</span>}
      <span className="shrink-0 font-mono text-xs">
        <ScoreText score={entry.score} />
      </span>
    </div>
  );
}

export default MultiplayerLeaderboard;
